import { MessageCircle, Plus } from 'lucide-react';
import { cn } from '@iheart/ui';
import { useChatRooms, type ChatRoom } from '../../lib/chat';
import { useChatWidgetStore } from './ChatWidgetStore';
import { TimeAgo } from '../TimeAgo';

/**
 * Workspace chats list. Domina AI has her own launcher bubble now, so this view
 * is purely teammate conversations + a (black) New-chat action.
 */
export function WidgetLobby() {
  const { goToThread, goToNewChat } = useChatWidgetStore();
  const rooms = useChatRooms();

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      <div className="min-h-0 flex-1 overflow-y-auto px-2 py-2">
        {!rooms.data ? (
          <div className="flex items-center justify-center py-8">
            <div className="size-5 animate-spin rounded-full border-2 border-pink-200 border-t-pink-500" />
          </div>
        ) : rooms.data.length === 0 ? (
          <div className="py-10 text-center">
            <MessageCircle className="mx-auto size-8 text-neutral-300" />
            <p className="mt-2 text-xs text-neutral-400">No workspace chats yet</p>
            <p className="text-[11px] text-neutral-400">Start one with a teammate below.</p>
          </div>
        ) : (
          <div className="space-y-0.5">
            {rooms.data.map((room) => (
              <RoomRow key={room.publicId} room={room} onClick={() => goToThread(room.publicId)} />
            ))}
          </div>
        )}
      </div>

      {/* New chat — black, unambiguously for teammate chats */}
      <div className="shrink-0 border-t border-neutral-100 p-2">
        <button
          type="button"
          onClick={goToNewChat}
          title="Start a new workspace chat with a teammate"
          className="flex w-full items-center justify-center gap-1.5 rounded-lg bg-neutral-900 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-neutral-800"
        >
          <Plus className="size-4" /> New chat
        </button>
      </div>
    </div>
  );
}

function RoomRow({ room, onClick }: { room: ChatRoom; onClick: () => void }) {
  const initial = (room.name ?? room.lastSenderName ?? '?').charAt(0).toUpperCase();

  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center gap-2.5 rounded-lg px-2 py-2 text-left transition-colors hover:bg-neutral-50"
    >
      <div className="flex size-9 shrink-0 items-center justify-center rounded-full bg-neutral-200 text-xs font-bold text-neutral-600">
        {initial}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between">
          <span className={cn('truncate text-sm', room.unreadCount > 0 ? 'font-semibold text-neutral-800' : 'font-medium text-neutral-700')}>
            {room.name ?? room.lastSenderName ?? 'Chat'}
          </span>
          {room.lastMessageAt && (
            <span className="ml-1 shrink-0 text-[10px] text-neutral-400">
              <TimeAgo date={room.lastMessageAt} />
            </span>
          )}
        </div>
        <div className="flex items-center justify-between">
          <span className="truncate text-[11px] text-neutral-400">
            {room.lastMessageBody ?? 'No messages yet'}
          </span>
          {room.unreadCount > 0 && (
            <span className="ml-1 flex size-5 shrink-0 items-center justify-center rounded-full bg-pink-500 text-[9px] font-bold text-white">
              {room.unreadCount > 99 ? '99+' : room.unreadCount}
            </span>
          )}
        </div>
      </div>
    </button>
  );
}
